import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { Box, Divider, Stack, Tooltip, Typography } from "@mui/material";
import type { FC } from "react";
import dayjs, { DATE_FORMAT, ISO_DATE_FORMAT } from "../../dayjs";
import type { SavedOrderProductWithNames } from "../../orders/orderTypes";

type Product = Omit<SavedOrderProductWithNames, "providerName" | "providerId">;

type GroupedProduct = {
  product: Product;
  idx: number;
};

type ProductGroup = {
  date: string;
  products: GroupedProduct[];
};

function groupProductsByDate(products: Product[]): ProductGroup[] {
  return Object.values(
    products.reduce<Record<string, ProductGroup>>((acc, product, idx) => {
      const key = dayjs(product.date, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT);

      (acc[key] ??= {
        date: product.date,
        products: [],
      }).products.push({ product, idx });

      return acc;
    }, {}),
  );
}

interface OrderProductsProps {
  readonly values: Product[];
  readonly limit?: number;
}
export const OrderProducts: FC<OrderProductsProps> = ({ values, limit }) => {
  const groupedProducts = groupProductsByDate(values);
  const limitedGroupedProducts = limit ? groupedProducts.slice(0, limit) : groupedProducts;
  return (
    <Box sx={{ borderLeft: "2px dashed #e0e0e0", pl: 3, ml: 1, py: 0.5, position: "relative" }}>
      <Stack spacing={1}>
        {limitedGroupedProducts.map((group) => (
          <Box key={group.date} sx={{ position: "relative" }}>
            <Box
              sx={{
                position: "absolute",
                left: -25,
                top: 5,
                transform: "translateX(-50%)",
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: "#ccc",
              }}
            />
            <Typography variant="caption" color="text.primary" sx={{ fontWeight: "bold" }}>
              {dayjs(group.date, ISO_DATE_FORMAT).format(DATE_FORMAT)}
            </Typography>
            {group.products.map(({ product: item, idx }) => (
              <Box key={idx}>
                <Box sx={{ pl: 1, mt: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="body2" color="text.primary">
                      {item.name}
                    </Typography>
                    {item.time && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                          alignItems: "center",
                          color: "text.secondary",
                          bgcolor: "rgba(0,0,0,0.04)",
                          px: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                          {item.time}
                        </Typography>
                      </Stack>
                    )}
                    {item.bookingNumber && (
                      <Tooltip
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="caption" sx={{ mb: 0.5, fontWeight: "bold", display: "block" }}>
                              Szczegóły rezerwacji
                            </Typography>
                            <Typography variant="body2">{item.name}</Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            bgcolor: "rgba(13, 110, 253, 0.08)",
                            color: "primary.main",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            cursor: "help",
                            border: "1px solid rgba(13, 110, 253, 0.2)",
                            "&:hover": {
                              bgcolor: "rgba(13, 110, 253, 0.15)",
                            },
                          }}
                        >
                          <ConfirmationNumberIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          {item.bookingNumber}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
              </Box>
            ))}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
